<script setup lang="ts">
/**
 * Which build is serving — shown to admins in the sidebar footer.
 *
 * Two jobs, and the second one is the useful one:
 *  1. say what is deployed (colour + commit + when),
 *  2. notice when THIS tab has gone stale. The SPA is a static build, so a tab
 *     left open across a deploy keeps running the old JavaScript against the new
 *     backend. That mismatch surfaces later as confusing, hard-to-reproduce API
 *     errors; here it surfaces immediately as "reload".
 *
 * The comparison is between the release baked into this bundle at build time
 * (runtimeConfig.public.sentryRelease, written by CI from CI_COMMIT_SHA) and the
 * release reported by the running backend.
 */
import client from '~/api/client'
import { useAuthStore } from '~/store/auth'

defineProps<{ collapsed?: boolean }>()
const authStore = useAuthStore()
const backendRelease = ref('')
const shortSha = ref('')
const title = ref('')
const colour = ref('')
const startedAt = ref('')
const failed = ref(false)

const ownRelease = String(useRuntimeConfig().public.sentryRelease || '')

// Only meaningful when both sides know their release — locally neither is set,
// and a half-known pair would produce a permanent false "stale" banner.
const isStale = computed(() => !!ownRelease && !!backendRelease.value && ownRelease !== backendRelease.value)

const dotColour = computed(() => {
  if (failed.value) return 'var(--ink-3)'
  if (isStale.value) return '#e0a800'
  return colour.value === 'blue' ? '#3b82f6' : '#22c55e'
})

const relativeTime = computed(() => {
  if (!startedAt.value) return ''
  const mins = Math.floor((Date.now() - new Date(startedAt.value).getTime()) / 60000)
  if (mins < 1) return 'právě teď'
  if (mins < 60) return `před ${mins} min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `před ${hours} h`
  return `před ${Math.floor(hours / 24)} dny`
})

async function load() {
  try {
    const { data } = await client.GET('/api/admin/version/')
    if (!data) {
      failed.value = true
      return
    }
    failed.value = false
    backendRelease.value = data.release
    shortSha.value = data.release_short
    title.value = data.release_title
    colour.value = data.colour
    startedAt.value = data.started_at
  } catch {
    failed.value = true
  }
}

// `window` is not on Vue's template globals allowlist, so the reload has to be a
// real function in scope — inlining it in @click would silently do nothing.
function reload() {
  window.location.reload()
}

let timer: ReturnType<typeof setInterval> | undefined
onMounted(() => {
  if (!authStore.user?.is_admin_role) return
  load()
  // Slow poll: this only needs to catch a deploy that happened while the tab was
  // open, and a deploy takes minutes.
  timer = setInterval(load, 60_000)
})
onUnmounted(() => timer && clearInterval(timer))
</script>

<template>
  <div v-if="authStore.user?.is_admin_role && (shortSha || failed)" class="wb-deploy">
    <div
      v-if="collapsed"
      class="wb-deploy__dot wb-deploy__dot--solo"
      :style="{ background: dotColour }"
      :title="isStale ? 'Nová verze — obnovte stránku' : `${colour} · ${shortSha}`"
    />

    <template v-else>
      <div class="wb-deploy__row" :title="backendRelease || 'verze nedostupná'">
        <span class="wb-deploy__dot" :style="{ background: dotColour }" />
        <span class="wb-deploy__colour">{{ failed ? 'neznámá verze' : colour || '—' }}</span>
        <code v-if="shortSha" class="wb-deploy__sha">{{ shortSha }}</code>
      </div>
      <div v-if="title" class="wb-deploy__title" :title="title">{{ title }}</div>
      <div v-if="relativeTime" class="wb-deploy__time">nasazeno {{ relativeTime }}</div>

      <button v-if="isStale" class="wb-deploy__reload" @click="reload">Nová verze — obnovit</button>
    </template>
  </div>
</template>

<style scoped>
.wb-deploy {
  padding: 8px 14px 0;
  font-family: var(--sans);
}
.wb-deploy__row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.wb-deploy__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.wb-deploy__dot--solo {
  margin: 0 auto;
  width: 8px;
  height: 8px;
}
.wb-deploy__colour {
  font-size: 11px;
  color: var(--ink-2);
  text-transform: capitalize;
}
.wb-deploy__sha {
  font-family: var(--mono, 'IBM Plex Mono', monospace);
  font-size: 10px;
  color: var(--ink-3);
}
.wb-deploy__title,
.wb-deploy__time {
  font-size: 10px;
  color: var(--ink-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.wb-deploy__title {
  margin-top: 2px;
}
.wb-deploy__reload {
  margin-top: 6px;
  width: 100%;
  padding: 5px 8px;
  border: 1px solid #e0a800;
  border-radius: var(--r-md);
  background: transparent;
  font-family: var(--sans);
  font-size: 11px;
  color: #a37400;
  cursor: pointer;
}
.wb-deploy__reload:hover {
  background: rgba(224, 168, 0, 0.08);
}
</style>
